import { Link } from 'react-router-dom';
import { Ship, Phone, Mail, MapPin, Anchor, Globe, ArrowRight } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Services', path: '/services' },
    { name: 'Track Shipment', path: '/tracking' },
    { name: 'Get a Quote', path: '/quote' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const services = [
    'Ocean Freight',
    'Container Shipping',
    'Bulk Cargo',
    'Project Cargo',
    'Warehousing',
    'Customs Clearance',
  ];

  return (
    <footer className="footer-section" data-testid="footer">
      {/* Main Footer */}
      <div className="rsl-container py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-safety-orange p-2">
                <Ship className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-black text-xl text-white tracking-tight">RSL</h3>
                <p className="text-[10px] font-mono tracking-widest uppercase text-white/50">
                  Robert Shipping & Logistics
                </p>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Delivering excellence across the oceans since 1975. Your trusted partner for global cargo transport and logistics solutions.
            </p>
            <div className="flex gap-3">
              <div className="cert-badge">
                <span>ISO 9001</span>
              </div>
              <div className="cert-badge">
                <span>ISO 14001</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-white uppercase tracking-widest text-sm mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="footer-link text-sm flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-safety-orange" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-white uppercase tracking-widest text-sm mb-6">
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-white/70 text-sm flex items-center gap-2">
                    <Anchor className="w-3 h-3 text-safety-orange" />
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-white uppercase tracking-widest text-sm mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-safety-orange flex-shrink-0 mt-0.5" />
                <span className="text-white/70 text-sm">
                  967 E 12th St<br />
                  Los Angeles, CA 90021
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-safety-orange flex-shrink-0" />
                <a
                  href="tel:+14153299700"
                  className="footer-link text-sm"
                  data-testid="footer-phone"
                >
                  (415) 329-9700
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-safety-orange flex-shrink-0" />
                <a
                  href="mailto:info@robertshipping.com"
                  className="footer-link text-sm"
                  data-testid="footer-email"
                >
                  info@robertshipping.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-safety-orange flex-shrink-0" />
                <span className="text-white/70 text-sm">
                  www.robertshipping.com
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="rsl-container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm font-mono">
              © {currentYear} Robert Shipping & Logistics. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/50 text-sm hover:text-safety-orange transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/50 text-sm hover:text-safety-orange transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Background Ship Graphic */}
      <div className="absolute bottom-0 right-0 opacity-5 pointer-events-none">
        <Ship className="w-96 h-96" />
      </div>
    </footer>
  );
};

export default Footer;
